import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Bot, Check, Copy, KeyRound, MoreHorizontal, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardFooter, CardHeader } from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogInset,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { BRAND_NAME } from "@/lib/brand";
import { trpc } from "@/lib/trpc";
import { cn, relativeTime } from "@/lib/utils";

export const Route = createFileRoute("/_app/settings")({
  component: SettingsPage,
});

function SettingsPage() {
  // Vercel's account settings: no heading in the content, just a stack of
  // setting cards under the top bar.
  return (
    <div className="flex w-full flex-col">
      <PageHeader title="Your settings" />
      <div className="grid gap-12 md:gap-16">
        <AdbKeys />
        <ApiKeys />
      </div>
    </div>
  );
}

/**
 * A key registered here is what makes `adb connect` silent.
 *
 * Without one, the first connect parks and asks whoever holds the device to
 * approve it — which works, but means bothering somebody and waiting on them.
 * The phone's own opinion of the key is no longer involved either way.
 *
 * Laid out as Vercel's Tokens page: a settings card that says what keys are
 * for and holds the way to add one, then the keys in a list card below it.
 */
function AdbKeys() {
  const qc = useQueryClient();
  const { data: keys } = useQuery(trpc.user.adbKeys.list.queryOptions());
  const [adding, setAdding] = useState(false);

  const refresh = () => qc.invalidateQueries({ queryKey: trpc.user.adbKeys.list.queryKey() });

  const remove = useMutation(
    trpc.user.adbKeys.remove.mutationOptions({
      onSuccess: () => {
        toast.success("Key removed");
        refresh();
      },
      onError: (e) => toast.error(e.message),
    }),
  );

  return (
    <section aria-labelledby="adb-keys-title" className="grid gap-6 md:gap-8">
      <Card>
        <CardHeader className="gap-4">
          <h2 id="adb-keys-title" className="text-gray-1000 text-heading-20">
            adb Keys
          </h2>
          <CardDescription>
            Registering your key lets you <code className="text-label-13-mono">adb connect</code> to
            any device you have reserved without asking anyone.
          </CardDescription>
        </CardHeader>
        <CardFooter className="flex-col items-stretch gap-3 py-4 sm:flex-row sm:items-center sm:justify-between sm:py-3">
          <p>A key identifies you: commands run through it are recorded against your account.</p>
          <Button size="small" className="pointer-coarse:h-10" onClick={() => setAdding(true)}>
            Add Key
          </Button>
        </CardFooter>
      </Card>

      <div className="overflow-hidden rounded-md bg-background-100 shadow-border">
        {keys === undefined ? (
          <div aria-hidden="true" className="divide-y divide-gray-alpha-400">
            {[0, 1].map((row) => (
              <div key={row} className="flex h-16 items-center gap-3 px-4">
                <Skeleton shape="circle" className="hidden size-8 sm:block" />
                <div className="grid flex-1 gap-2">
                  <Skeleton className="h-3.5 w-32" />
                  <Skeleton className="h-3 w-72 max-w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : keys.length > 0 ? (
          <>
            <div className="flex h-12 items-center gap-3 border-gray-alpha-400 border-b px-4 text-gray-900 text-label-14">
              <span className="flex-1">Key</span>
              <span className="hidden sm:block">Last used</span>
              {/* Holds the column the rows' menu buttons sit in. */}
              <span aria-hidden="true" className="w-8 shrink-0 pointer-coarse:w-10" />
            </div>
            <ul className="divide-y divide-gray-alpha-400">
              {keys.map((key) => (
                <li key={key.id} className="flex min-h-16 items-center gap-3 px-4 py-3">
                  <div className="hidden size-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-1000 sm:flex">
                    <KeyRound className="size-4" />
                  </div>
                  <div className="grid min-w-0 flex-1">
                    <div className="flex min-w-0 items-baseline gap-2">
                      <span className="truncate text-gray-1000 text-label-14">{key.title}</span>
                      {key.comment && (
                        <span className="hidden truncate text-gray-900 text-label-13 sm:inline">
                          {key.comment}
                        </span>
                      )}
                    </div>
                    {/* Wrapped, not truncated: a fingerprint is for comparing
                        against `adb`'s output, and half of one cannot be. */}
                    <code className="break-all text-gray-900 text-label-13-mono">
                      {key.fingerprint}
                    </code>
                    {/* On a phone the comment and last use share a line under the
                        fingerprint, so the name keeps the first line to itself. */}
                    <p className="flex min-w-0 items-baseline gap-1.5 text-gray-900 text-label-13 sm:hidden">
                      {key.comment && (
                        <>
                          <span className="truncate">{key.comment}</span>
                          <span aria-hidden="true">·</span>
                        </>
                      )}
                      <LastUsed at={key.lastUsedAt} className="shrink-0" />
                    </p>
                  </div>
                  {/* The column is headed "Last used", so the cell is just the time. */}
                  <LastUsed
                    at={key.lastUsedAt}
                    bare
                    className="hidden shrink-0 text-label-14 first-letter:uppercase sm:block"
                  />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="tertiary"
                        size="icon-sm"
                        className="shrink-0 pointer-coarse:size-10"
                        aria-label={`Actions for ${key.title}`}
                      >
                        <MoreHorizontal />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-44">
                      <DropdownMenuItem
                        variant="error"
                        aria-label={`Remove ${key.title}`}
                        disabled={remove.isPending}
                        onSelect={() => remove.mutate({ id: key.id })}
                      >
                        Remove Key
                        <Trash2 className="ml-auto" />
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </li>
              ))}
            </ul>
          </>
        ) : (
          // Vercel's text-only empty state inside the list it stands in for.
          <div className="flex flex-col items-center gap-1 px-6 py-10 text-center">
            <p className="text-gray-1000 text-heading-14">No keys yet</p>
            <p className="max-w-sm text-balance text-copy-14 text-gray-900">
              Until you add one, an <code className="text-label-13-mono">adb connect</code> waits
              for the device holder to approve it.
            </p>
          </div>
        )}
      </div>

      <AddAdbKeyDialog open={adding} onOpenChange={setAdding} onAdded={refresh} />
    </section>
  );
}

/**
 * Keys for agents and scripts — what the MCP server signs in with.
 *
 * Same layout as the adb keys above. A key is shown once, at creation, because
 * only its hash is kept.
 */
function ApiKeys() {
  const qc = useQueryClient();
  const { data: keys } = useQuery(trpc.user.apiKeys.list.queryOptions());
  const [creating, setCreating] = useState(false);

  const refresh = () => qc.invalidateQueries({ queryKey: trpc.user.apiKeys.list.queryKey() });

  const remove = useMutation(
    trpc.user.apiKeys.remove.mutationOptions({
      onSuccess: () => {
        toast.success("Key revoked");
        refresh();
      },
      onError: (e) => toast.error(e.message),
    }),
  );

  return (
    <section aria-labelledby="api-keys-title" className="grid gap-6 md:gap-8">
      <Card>
        <CardHeader className="gap-4">
          <h2 id="api-keys-title" className="text-gray-1000 text-heading-20">
            API Keys
          </h2>
          <CardDescription>
            Let an AI agent reserve and drive devices through {BRAND_NAME}'s MCP server, as you.
          </CardDescription>
        </CardHeader>
        <CardFooter className="flex-col items-stretch gap-3 py-4 sm:flex-row sm:items-center sm:justify-between sm:py-3">
          <p>A key can do what you can on a device, but not admin actions or manage keys.</p>
          <Button size="small" className="pointer-coarse:h-10" onClick={() => setCreating(true)}>
            Create Key
          </Button>
        </CardFooter>
      </Card>

      <div className="overflow-hidden rounded-md bg-background-100 shadow-border">
        {keys === undefined ? (
          <div aria-hidden="true" className="divide-y divide-gray-alpha-400">
            {[0, 1].map((row) => (
              <div key={row} className="flex h-16 items-center gap-3 px-4">
                <Skeleton shape="circle" className="hidden size-8 sm:block" />
                <div className="grid flex-1 gap-2">
                  <Skeleton className="h-3.5 w-32" />
                  <Skeleton className="h-3 w-40 max-w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : keys.length > 0 ? (
          <>
            <div className="flex h-12 items-center gap-3 border-gray-alpha-400 border-b px-4 text-gray-900 text-label-14">
              <span className="flex-1">Key</span>
              <span className="hidden sm:block">Last used</span>
              <span aria-hidden="true" className="w-8 shrink-0 pointer-coarse:w-10" />
            </div>
            <ul className="divide-y divide-gray-alpha-400">
              {keys.map((key) => (
                <li key={key.id} className="flex min-h-16 items-center gap-3 px-4 py-3">
                  <div className="hidden size-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-1000 sm:flex">
                    <Bot className="size-4" />
                  </div>
                  <div className="grid min-w-0 flex-1">
                    <span className="truncate text-gray-1000 text-label-14">{key.title}</span>
                    <code className="text-gray-900 text-label-13-mono">{key.start}…</code>
                    <p className="text-gray-900 text-label-13 sm:hidden">
                      <LastUsed at={key.lastUsedAt} />
                    </p>
                  </div>
                  <LastUsed
                    at={key.lastUsedAt}
                    bare
                    className="hidden shrink-0 text-label-14 first-letter:uppercase sm:block"
                  />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="tertiary"
                        size="icon-sm"
                        className="shrink-0 pointer-coarse:size-10"
                        aria-label={`Actions for ${key.title}`}
                      >
                        <MoreHorizontal />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-44">
                      <DropdownMenuItem
                        variant="error"
                        aria-label={`Revoke ${key.title}`}
                        disabled={remove.isPending}
                        onSelect={() => remove.mutate({ id: key.id })}
                      >
                        Revoke Key
                        <Trash2 className="ml-auto" />
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <div className="flex flex-col items-center gap-1 px-6 py-10 text-center">
            <p className="text-gray-1000 text-heading-14">No keys yet</p>
            <p className="max-w-sm text-balance text-copy-14 text-gray-900">
              Create one to connect an agent such as Claude Code.
            </p>
          </div>
        )}
      </div>

      <CreateApiKeyDialog open={creating} onOpenChange={setCreating} onCreated={refresh} />
    </section>
  );
}

function CreateApiKeyDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState("");
  const [secret, setSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState<"key" | "command" | null>(null);

  const create = useMutation(
    trpc.user.apiKeys.create.mutationOptions({
      onSuccess: (created) => {
        setSecret(created.secret);
        onCreated();
      },
      onError: (e) => toast.error(e.message),
    }),
  );

  // The secret is dropped with the dialog: it is never shown twice.
  const close = () => {
    onOpenChange(false);
    setTitle("");
    setSecret(null);
    setCopied(null);
  };

  const command = secret
    ? `claude mcp add yard -e YARD_URL=${window.location.origin} -e YARD_API_KEY=${secret} -- yard-mcp`
    : "";

  const copy = async (what: "key" | "command", text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(what);
    toast.success("Copied");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent showCloseButton={false}>
        {secret ? (
          <>
            <DialogHeader>
              <DialogTitle>Key created</DialogTitle>
              <DialogDescription>
                Only a hash is stored, so it can't be shown again. If it is lost, create a new one
                and revoke this one.
              </DialogDescription>
            </DialogHeader>
            <DialogInset className="grid gap-5">
              <div className="grid gap-2">
                <Label htmlFor="api-key-secret">Key</Label>
                <Input
                  id="api-key-secret"
                  readOnly
                  value={secret}
                  className="h-10 [&>input]:h-full [&>input]:font-mono [&>input]:text-[13px] pointer-coarse:[&>input]:text-[13px]"
                  onFocus={(e) => e.currentTarget.select()}
                  suffixStyling={false}
                  suffix={
                    <Button
                      variant="tertiary"
                      size="icon-sm"
                      className="-mr-1 text-gray-900 hover:text-gray-1000"
                      aria-label={copied === "key" ? "Copied" : "Copy key"}
                      onClick={() => copy("key", secret)}
                    >
                      {copied === "key" ? <Check /> : <Copy />}
                    </Button>
                  }
                />
              </div>
              <div className="grid gap-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="api-key-command">Add it to Claude Code</Label>
                  <Button
                    variant="tertiary"
                    size="small"
                    onClick={() => copy("command", command)}
                    aria-label={copied === "command" ? "Copied" : "Copy command"}
                  >
                    {copied === "command" ? <Check /> : <Copy />}
                    Copy
                  </Button>
                </div>
                <Textarea
                  id="api-key-command"
                  readOnly
                  rows={4}
                  spellCheck={false}
                  className="break-all font-mono md:text-copy-13"
                  value={command}
                />
              </div>
            </DialogInset>
            <DialogFooter>
              <Button variant="secondary" onClick={close}>
                Done
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Create API Key</DialogTitle>
              <DialogDescription>
                Name it after what will use it, so you know what breaks when you revoke it.
              </DialogDescription>
            </DialogHeader>
            <form
              className="contents"
              onSubmit={(event) => {
                event.preventDefault();
                create.mutate({ title });
              }}
            >
              <DialogInset className="grid gap-2">
                <Label htmlFor="api-key-title">Name</Label>
                <Input
                  id="api-key-title"
                  required
                  maxLength={100}
                  placeholder="Claude Code on my laptop"
                  className="h-10"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                />
              </DialogInset>
              <DialogFooter>
                <DialogClose asChild>
                  <Button type="button" variant="secondary">
                    Cancel
                  </Button>
                </DialogClose>
                <Button type="submit" loading={create.isPending} disabled={!title.trim()}>
                  Create Key
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LastUsed({
  at,
  bare = false,
  className,
}: {
  at: Date | string | null;
  bare?: boolean;
  className?: string;
}) {
  return (
    <span className={cn("text-gray-900 tabular-nums", className)}>
      {/* Last used, not added: the question people actually have about an old
          key is whether anything still relies on it. */}
      {at ? `${bare ? "" : "Last used "}${relativeTime(at)}` : bare ? "Never" : "Never used"}
    </span>
  );
}

function AddAdbKeyDialog({
  open,
  onOpenChange,
  onAdded,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onAdded: () => void;
}) {
  const [publicKey, setPublicKey] = useState("");
  const [title, setTitle] = useState("");

  const add = useMutation(
    trpc.user.adbKeys.add.mutationOptions({
      onSuccess: () => {
        toast.success("Key added");
        setPublicKey("");
        setTitle("");
        onAdded();
        onOpenChange(false);
      },
      onError: (e) => toast.error(e.message),
    }),
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Add adb Key</DialogTitle>
          {/* Most people have never opened this file, so the command to print
              it is part of the instruction rather than something to look up. */}
          <DialogDescription>
            Run <code className="text-label-14-mono">cat ~/.android/adbkey.pub</code> and paste the
            whole line. That is the public half — never paste{" "}
            <code className="text-label-14-mono">adbkey</code> itself.
          </DialogDescription>
        </DialogHeader>

        {/* `contents`, so the band and the footer stay the dialog's own
            children and keep its full-bleed layout. */}
        <form
          className="contents"
          onSubmit={(event) => {
            event.preventDefault();
            add.mutate({ publicKey, title });
          }}
        >
          <DialogInset className="grid gap-5">
            <div className="grid gap-2">
              <Label htmlFor="adb-key">Public Key</Label>
              <Textarea
                id="adb-key"
                required
                rows={3}
                spellCheck={false}
                className="break-all font-mono md:text-copy-13"
                placeholder="QAAAA… user@host"
                value={publicKey}
                onChange={(event) => setPublicKey(event.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="adb-key-title">Name</Label>
              <Input
                id="adb-key-title"
                required
                maxLength={100}
                placeholder="Work laptop"
                className="h-10"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
            </div>
          </DialogInset>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              loading={add.isPending}
              disabled={!publicKey.trim() || !title.trim()}
            >
              Add Key
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
